import { useTranslation } from "next-i18next";
import clsx from "clsx";
import React from "react";

import LoadingIndicator from "@/components/ui/svg/LoadingIndicator";
import CloseButton from "@/components/ui/svg/CloseButton";
import { useDebouncedSearch } from "@/utils/hooks";

/** Props of {@link SearchFilter} */
interface Props<T> {
  /** Label of the search box */
  label: string;
  /** Component displaying already selected items */
  selected?: React.ReactNode;
  /** Search function */
  searchFunction?: (text: string, ...args: unknown[]) => Promise<T | void>;
  /** Function to display the search function's results */
  children?: (results: T | undefined | void) => React.ReactNode;
}

/**
 * Renders an input to execute a debounced search on the given function.
 * Passes the results to its children to be displayed.
 *
 * @param props - Component props
 */
export default function SearchFilter<T>({ label, selected, searchFunction, children }: Props<T>) {
  const { t } = useTranslation("common");

  const { inputText, setInputText, searchResults } = useDebouncedSearch(
    searchFunction || (async () => undefined)
  );

  return (
    <div className="flex flex-col w-full sm:w-[384px] text-left" onClick={(e) => e.stopPropagation()}>
      <span className="text-xs leading-1.25 tracking-wider uppercase opacity-60 mb-3">{label}</span>
      {selected}

      {searchFunction && (
        <div className="h-10 flex items-center border-b border-dark-6 mb-3">
          <input
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            className="grow h-full bg-transparent outline-none"
            placeholder={t("common.search.placeholder")}
          />
          {inputText && (
            <button
              onClick={() => setInputText("")}
              className={clsx(
                "shrink-0 w-5 h-5 rounded-full flex items-center",
                "justify-center bg-dark-8 text-dark-6"
              )}
            >
              {searchResults.loading ? (
                <LoadingIndicator width="12" height="12" />
              ) : (
                <CloseButton width="12" height="12" />
              )}
            </button>
          )}
        </div>
      )}

      {children?.(searchResults.result)}
    </div>
  );
}
